// The project's pin layer on a desktop: hydrated on open, kept to live's committed values, written back to the project.
import { createListenerMiddleware } from '@reduxjs/toolkit'
import { getProperty as getProp } from 'dot-prop'
import { IS_OUTPUT } from '~/lib/constants'
import paramChannel from '~/lib/paramChannel'
import { parseLivePath, schemaForLivePath, settleSourceValues, sourceParams, vocabPinId } from '~/lib/params'
import { PIN_IDENTITY, parsePins, pinPath, pinValue } from '~/lib/pinLayer'
import { isShaderSource } from '~/lib/shaders'
import { fillSourcesOf } from '~/lib/sourceFill'
import api from '~/store/api'
import { selectClientMode, selectIsTemplate, selectProject, selectProjectId } from '~/store/app/selectors'
import { hydratePins, setPinValues } from '~/store/playback'
import { selectLiveProps } from '~/store/playback/selectors'

const WRITE_MS = 500

// A remote mirrors the runner's layer; a camera phone and an output window have none.
export const isDesktop = s => !IS_OUTPUT && !!s.app && !selectClientMode(s)

const same = (a, b) => a === b || JSON.stringify(a) === JSON.stringify(b)

// A live commit is the value; on a pin the channel wins, since a mid-fade pin froze there and the channel trails a commit.
const valueOf = (s, key, committed) => {
	if (!committed && paramChannel.has(key)) return paramChannel.get(key)
	const v = getProp(s.live, key)
	if (v !== undefined) return v
	return schemaForLivePath(key, s.live?.objects)?.initial ?? null
}

// The shaders shown that declare `id`, the selected shapes' first, then the last fill touched: a new pin reads the one being edited.
const vocabSources = (s, id) => {
	const objects = s.live.objects ?? {}
	const first = [...(s.selection?.objectIds ?? []), s.editor?.last_fill_id].map(o => objects[o]?.fill?.source)
	return [...new Set([...first, ...fillSourcesOf(objects)])].filter(
		src => isShaderSource(src) && sourceParams(src)?.[id]
	)
}

// A vocabulary pin follows whichever shader's `id` this commit moved; a new one reads the edited shader's. Undefined when neither. A shader holding the pin clamped to its own narrower range did not move it.
const vocabValueOf = (s, was, id, held) => {
	const sources = vocabSources(s, id)
	const at = src => s.live.source_settings?.[src]?.[id] ?? sourceParams(src)[id].initial
	const clamped = src => settleSourceValues(src, { [id]: held })?.[id] ?? sourceParams(src)[id].initial
	const moved = sources.find(
		src =>
			s.live.source_settings?.[src]?.[id] !== was?.source_settings?.[src]?.[id] &&
			(held === undefined || !same(at(src), clamped(src)))
	)
	if (moved) return at(moved)
	return held === undefined && sources.length ? at(sources[0]) : undefined
}

const pinsSync = createListenerMiddleware()

// Once per project: a refetch of the open one keeps this desktop's layer.
pinsSync.startListening({
	predicate: (action, s) => {
		if (!isDesktop(s)) return false
		const id = selectProjectId(s)
		return !!id && s.playback.pins_project !== id && (!!selectProject(s) || selectIsTemplate(s))
	},
	effect: (action, { getState, dispatch }) => {
		flush(dispatch)
		const s = getState()
		dispatch(hydratePins({ project_id: selectProjectId(s), pins: parsePins(selectProject(s)?.pins) }))
	},
})

pinsSync.startListening({
	predicate: (action, s, was) =>
		isDesktop(s) && s.playback.pins_loaded && (s.live !== was.live || s.playback.pins !== was.playback.pins),
	effect: (action, { getState, getOriginalState, dispatch }) => {
		const s = getState()
		const was = getOriginalState().live
		const committed = s.live !== was
		const out = []
		for (const key of selectLiveProps(s)) {
			const p = parseLivePath(key)
			const id = vocabPinId(key)
			if (id) {
				const held = pinValue(s.playback.pins, key)
				const v = vocabValueOf(s, committed ? was : s.live, id, held)
				if (v !== undefined && !same(v, held)) out.push([pinPath(key), v])
				continue
			}
			if (!p?.existence) {
				// An object a preset dropped keeps its pinned values for the load that brings it back.
				if (p?.scope === 'object' && !s.live.objects?.[p.id]) continue
				const held = pinValue(s.playback.pins, key)
				// Only a new pin reads outside a commit: the channel trails a commit.
				if (!committed && held !== undefined) continue
				const v = valueOf(s, key, committed)
				if (v !== null && !same(v, held)) out.push([pinPath(key), v])
				continue
			}
			const obj = s.live.objects?.[p.id]
			if (!obj) continue
			for (const f of PIN_IDENTITY)
				if (obj[f] !== s.playback.pins.objects?.[p.id]?.[f]) out.push([['objects', p.id, f], obj[f]])
		}
		if (out.length) dispatch(setPinValues(out))
	},
})

let pending = null
let timer = null

const flush = dispatch => {
	clearTimeout(timer)
	timer = null
	if (!pending) return
	const { id, pins } = pending
	pending = null
	// `updateProject` patches only the open project's cache, and a switch may already have left this one.
	dispatch(
		api.util.updateQueryData('getProject', id, d => {
			if (d?.project) d.project.pins = pins
		})
	)
	dispatch(api.endpoints.updateProject.initiate({ id, pins }))
}

// A template is never saved over.
pinsSync.startListening({
	predicate: (action, s, was) =>
		isDesktop(s) &&
		!hydratePins.match(action) &&
		s.playback.pins !== was.playback.pins &&
		!!s.playback.pins_project &&
		!selectIsTemplate(s),
	effect: (action, { getState, dispatch }) => {
		const { pins, pins_project } = getState().playback
		if (pending && pending.id !== pins_project) flush(dispatch)
		pending = { id: pins_project, pins }
		clearTimeout(timer)
		timer = setTimeout(() => flush(dispatch), WRITE_MS)
	},
})

export default pinsSync
